import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type CountryPack = {
  id: string;
  packId: string;
  version: string;
  country: string;
  displayName: string;
  enabled: boolean;
  enabledAt: string | null;
  isBundled: boolean;
  bundledFromPath: string | null;
  lastSeenAt: string;
  manifest: unknown;
};

const PACKS_KEY = ['country-packs'] as const;
const PROFILES_KEY = ['terminal-management', 'profiles'] as const;

export function useCountryPacks(filter?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...PACKS_KEY, filter ?? {}] as const,
    queryFn: async (): Promise<CountryPack[]> => {
      const res = await apiClient.get<CountryPack[]>('/country-packs', {
        params: filter?.enabled === undefined ? {} : { enabled: filter.enabled },
      });
      return res.data;
    },
  });
}

export function useSetCountryPackEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; enabled: boolean }): Promise<CountryPack> => {
      const res = await apiClient.patch<CountryPack>(`/country-packs/${input.id}`, {
        enabled: input.enabled,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PACKS_KEY }),
  });
}

export function useBindProfileCountryPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { profileId: string; packId: string }) => {
      const res = await apiClient.post<{
        profileId: string;
        packId: string;
        packVersion: string;
      }>(`/terminal-profiles/${input.profileId}/country-pack`, { packId: input.packId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROFILES_KEY });
      qc.invalidateQueries({ queryKey: PACKS_KEY });
    },
  });
}

export function useUnbindProfileCountryPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profileId: string): Promise<void> => {
      await apiClient.delete(`/terminal-profiles/${profileId}/country-pack`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROFILES_KEY });
      qc.invalidateQueries({ queryKey: PACKS_KEY });
    },
  });
}
